/**
 * Parsing and formatting for the delay strings the builder stores on a message,
 * e.g. `"90m"`, `"2d"`, `"-30m"` (negative = before the anchor event).
 *
 * Extracted verbatim from SchedulingSection so the arithmetic is testable
 * without rendering, and so the encoding is understood in one place — the
 * message list used to re-parse it independently.
 */

export const MINUTES_IN_DAY = 1440;

export function roundToNearest5(x: number): number {
  return Math.round(x / 5) * 5;
}

/** Negative delays run before the anchor event (reminders, annual "before"). */
export function isDelayBefore(delay: string): boolean {
  return delay.startsWith('-');
}

/** The numeric part of a delay, sign and unit suffix stripped. */
export function getDelayInMinutes(delay: string): number {
  const raw = delay.replace('-', '').slice(0, -1);
  return Math.abs(Number(raw));
}

export function getDelayDays(delay: string | undefined): number {
  if (!delay) return 0;
  const delayInMinutes = getDelayInMinutes(delay);
  const isBefore = isDelayBefore(delay);
  return isBefore
    ? Math.ceil(Math.abs(delayInMinutes) / MINUTES_IN_DAY)
    : Math.floor(Math.abs(delayInMinutes) / MINUTES_IN_DAY);
}

export function getDelayTime(delay: string | undefined): number {
  if (!delay) return 540;
  const delayInMinutes = getDelayInMinutes(delay);
  const isBefore = isDelayBefore(delay);
  const time = !isBefore
    ? roundToNearest5(delayInMinutes % MINUTES_IN_DAY)
    : roundToNearest5(
        delayInMinutes > MINUTES_IN_DAY
          ? delayInMinutes % MINUTES_IN_DAY
          : MINUTES_IN_DAY % delayInMinutes,
      );
  return delayInMinutes < MINUTES_IN_DAY ? time : isBefore ? MINUTES_IN_DAY - time : time;
}

export function buildAnnualDelay(pastOrFuture: string, days: number, timeMinutes: number): string {
  const value =
    pastOrFuture === 'after'
      ? days * MINUTES_IN_DAY + Math.abs(timeMinutes)
      : -days * MINUTES_IN_DAY + Math.abs(timeMinutes);
  return `${pastOrFuture === 'before' && days > 0 ? '-' : ''}${Math.abs(value)}m`;
}

/** The unit suffix of a delay string (`m` | `h` | `d`), for the period select. */
export function getDelayPeriod(delay: string | undefined, fallback: string): string {
  if (!delay) return fallback;
  return delay[delay.length - 1];
}

/** The numeric part as the user types it — sign and unit removed. */
export function getDelayAmount(delay: string | undefined): string {
  if (!delay) return '';
  return delay.replace('-', '').slice(0, -1);
}
